import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ReviewResponse } from '@/api/types';
import { Save, RefreshCw, Activity } from 'lucide-react';
import { FileLinks } from '@/components/common/FileLinks';
import { AnalysisOverviewTab as OverviewTab } from '@/components/ai-tools/analysis/AnalysisOverviewTab';
import { AnalysisSuggestionsTab as SuggestionsTab } from '@/components/ai-tools/analysis/AnalysisSuggestionsTab';
import { AnalysisKeywordsTab as KeywordsTab } from '@/components/ai-tools/analysis/AnalysisKeywordsTab';
import { AnalysisCoverageTab as CoverageTab } from '@/components/ai-tools/analysis/AnalysisCoverageTab';
import { AnalysisHRInsightsTab as HRInsightsTab } from '@/components/ai-tools/analysis/AnalysisHRInsightsTab';
import { AnalysisRecruiterNotesTab as RecruiterNotesTab } from '@/components/ai-tools/analysis/AnalysisRecruiterNotesTab';
import { Button } from '@/components/ui/Button';

interface AnalysisResultsProps {
  result: ReviewResponse;
  onSave: () => void;
  isSaving: boolean;
  activeTab?: string;
  resume?: any;
  jobText?: string;
  onRetry?: () => void;
}

export function Analysis({ result, onSave, isSaving, activeTab: initialTab, onRetry }: AnalysisResultsProps) {
  const [activeTab, setActiveTab] = useState(initialTab || 'overview');
  
  const { review, files } = result;

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'sections', label: 'Suggestions' },
    { id: 'keywords', label: 'Keywords' },
    { id: 'coverage', label: 'Coverage' },
    { id: 'hr', label: 'HR Insights' },
    { id: 'notes', label: 'Recruiter Notes' },
  ];

  return (
    <div className="flex flex-col flex-1 bg-white dark:bg-slate-900 relative rounded-xl border border-slate-200 dark:border-slate-800 h-full overflow-hidden dark:shadow-sm min-h-0">
      <div className="flex items-center justify-between px-4 lg:px-8 py-4 lg:py-5 border-b border-slate-300 dark:border-slate-800 bg-white dark:bg-slate-900 z-10 flex-col sm:flex-row gap-4 sm:gap-2 rounded-t-xl shrink-0">
        <div className="flex items-center gap-2 text-slate-900 dark:text-white">
          <Activity className="w-5 h-5 text-emerald-600 dark:text-emerald-500" />
          <h2 className="text-sm font-semibold tracking-tight w-full sm:w-auto text-center sm:text-left">Analysis Results</h2>
        </div>
        {onRetry && (
          <Button
            variant="ghost"
            onClick={onRetry}
            className="flex-1 sm:flex-none h-auto py-1.5 px-3 min-h-[36px]"
          >
            <RefreshCw className="w-4 h-4 shrink-0" />
            <span className="whitespace-nowrap ml-1.5 inline">Retry</span>
          </Button>
        )}
      </div>
      <div className="flex space-x-2 px-4 lg:px-8 border-b border-slate-300 dark:border-slate-800 overflow-x-auto scrollbar-hide bg-white dark:bg-slate-900 pt-2 shrink-0">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`px-4 py-3 text-sm font-semibold transition-colors whitespace-nowrap focus:outline-none border-b-2 cursor-pointer ${
                activeTab === t.id
                  ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400'
                  : 'border-transparent text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:border-slate-300 dark:hover:border-slate-700'
              }`}
            >
              {t.label}
            </button>
          ))}
      </div>

      <div className="flex-1 px-4 pt-4 lg:px-8 lg:pt-8 overflow-y-auto overflow-x-hidden">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="flex flex-col min-h-full pb-4 lg:pb-8"
          >
            {activeTab === 'overview' && (
              <OverviewTab report={review.atsReport} recommendations={review.sectionRecommendations} />
            )}

            {activeTab === 'sections' && (
              <SuggestionsTab recommendations={review.sectionRecommendations} />
            )}

            {activeTab === 'keywords' && (
              <KeywordsTab report={review.atsReport} />
            )}

            {activeTab === 'coverage' && (
              <CoverageTab report={review.atsReport} />
            )}

            {activeTab === 'hr' && review.hrInsights && (
              <HRInsightsTab insights={review.hrInsights} />
            )}

            {activeTab === 'notes' && (
              <RecruiterNotesTab notes={review.recruiterNotes} />
            )}

            <div className="mt-8 pt-4 border-t border-slate-300 dark:border-slate-800">
              <FileLinks files={files} />
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

